import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Mail, Phone, Building2, X } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PartnershipsPage({ admin, onLogout }) {
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formData, setFormData] = useState({
    organization_name: '',
    contact_person: '',
    email: '',
    phone: '',
    partnership_type: 'pedagogical',
    status: 'active',
    notes: ''
  });

  useEffect(() => {
    fetchPartnerships();
  }, []);

  const fetchPartnerships = async () => {
    try {
      const response = await axios.get(`${API}/partnerships`);
      setPartnerships(response.data);
    } catch (error) {
      console.error('Error fetching partnerships:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/partnerships`, formData);
      fetchPartnerships();
      resetForm();
    } catch (error) {
      console.error('Error saving partnership:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const resetForm = () => {
    setFormData({
      organization_name: '',
      contact_person: '',
      email: '',
      phone: '',
      partnership_type: 'pedagogical',
      status: 'active',
      notes: ''
    });
    setEditingId(null);
    setShowForm(false);
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-700',
      inactive: 'bg-gray-100 text-gray-700',
      pending: 'bg-yellow-100 text-yellow-700'
    };
    
    const labels = {
      active: 'Actif',
      inactive: 'Inactif',
      pending: 'En attente'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badges[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const badges = {
      financial: 'bg-blue-100 text-blue-700',
      pedagogical: 'bg-purple-100 text-purple-700',
      both: 'bg-violet-100 text-violet-700'
    };
    
    const labels = {
      financial: 'Financier',
      pedagogical: 'Pédagogique',
      both: 'Financier & Pédagogique'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badges[type]}`}>
        {labels[type]}
      </span>
    );
  };

  return (
    <AdminLayout admin={admin} onLogout={onLogout} title="Gestion des Partenariats">
      <div className="space-y-6">
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 btn-violet"
            data-testid="add-partnership-button"
          >
            <Plus size={20} />
            Nouveau Partenariat
          </button>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">
                  {editingId ? 'Modifier le partenariat' : 'Nouveau partenariat'}
                </h3>
                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Organisation *</label>
                    <input
                      type="text"
                      value={formData.organization_name}
                      onChange={(e) => setFormData({...formData, organization_name: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="partnership-org-input"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Personne de contact *</label>
                    <input
                      type="text"
                      value={formData.contact_person}
                      onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="partnership-contact-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="partnership-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone *</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="partnership-phone-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
                    <select
                      value={formData.partnership_type}
                      onChange={(e) => setFormData({...formData, partnership_type: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="partnership-type-select"
                    >
                      <option value="pedagogical">Pédagogique</option>
                      <option value="financial">Financier</option>
                      <option value="both">Les deux</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Statut *</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="partnership-status-select"
                    >
                      <option value="active">Actif</option>
                      <option value="pending">En attente</option>
                      <option value="inactive">Inactif</option>
                    </select>
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      className="input-violet"
                      rows="3"
                      data-testid="partnership-notes-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="submit" className="btn-violet flex-1" data-testid="save-partnership-button">
                    {editingId ? 'Mettre à jour' : 'Créer'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Chargement...</p>
          </div>
        ) : partnerships.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">Aucun partenariat</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {partnerships.map((partnership) => (
              <div key={partnership.id} className="card-violet" data-testid={`partnership-card-${partnership.id}`}>
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold text-gray-800 flex-1">
                    {partnership.organization_name}
                  </h3>
                  {getStatusBadge(partnership.status)}
                </div>

                <div className="mb-4">
                  {getTypeBadge(partnership.partnership_type)}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Building2 size={16} className="text-violet-500" />
                    <span>{partnership.contact_person}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail size={16} className="text-violet-500" />
                    <span className="truncate">{partnership.email}</span>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone size={16} className="text-violet-500" />
                    <span>{partnership.phone}</span>
                  </div>
                </div>

                {partnership.notes && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{partnership.notes}</p>
                )}

                <div className="flex gap-2 pt-4 border-t border-gray-100">
                  <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-violet-50 text-violet-600 rounded-lg hover:bg-violet-100 transition-colors">
                    <Edit size={16} />
                    Modifier
                  </button>
                  <button className="flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}